import { useState, useRef, useCallback, lazy, Suspense } from 'react'
import { API_BASE, authorizationHeader } from '@/lib/api'
import { Modal } from '@/shared/components/Modal'

const AccessTokenModal = lazy(() =>
  import('@/shared/components/AccessTokenModal').then((m) => ({ default: m.AccessTokenModal })),
)

type Stage = 'idle' | 'uploading' | 'done' | 'error'

interface Props {
  slug: string
  onClose: () => void
  onSuccess: () => void
}

export function PublishModal({ slug, onClose, onSuccess }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [form, setForm] = useState({ name: '', version: '' })
  const [stage, setStage] = useState<Stage>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [showTokenHelp, setShowTokenHelp] = useState(false)

  const acceptFile = useCallback((f: File) => {
    if (!f.name.endsWith('.tar.gz') && !f.name.endsWith('.tgz')) {
      setError('File must be a .tar.gz archive.')
      return
    }
    setFile(f)
    setError(null)
    const match = f.name.match(/^(.+)-(\d[\d.]*)\.(?:tar\.gz|tgz)$/)
    if (match) {
      setForm((prev) => ({ ...prev, name: match[1], version: match[2] }))
    }
  }, [])

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) acceptFile(f)
  }

  async function handlePublish(e: React.FormEvent) {
    e.preventDefault()
    if (!file) { setError('Please select a .tar.gz file.'); return }
    setError(null)
    setStage('uploading')
    try {
      await uploadWithProgress(
        `${API_BASE}/v1/modules/${slug}/${form.name}/_/${form.version}`,
        file,
        setProgress,
        await authorizationHeader(),
      )
      setStage('done')
    } catch (err) {
      setError((err as Error).message)
      setStage('error')
    }
  }

  const curlUrl = `${API_BASE}/v1/modules/${slug}/<name>/<provider>/<version>`

  return (
    <>
      {showTokenHelp && (
        <Suspense fallback={null}>
          <AccessTokenModal onClose={() => setShowTokenHelp(false)} />
        </Suspense>
      )}
      <Modal track="publish_module" title="Publish module" kicker={`// ${slug}`} onClose={onClose} maxWidth={900}>
        {stage === 'done' ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
            <div style={{ color: 'var(--green)', fontFamily: 'var(--mono)', marginBottom: 8 }}>
              v{form.version} published
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginBottom: 24 }}>
              {slug}/{form.name}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => {
                setStage('idle'); setFile(null); setProgress(0)
                setForm((f) => ({ ...f, version: '' }))
              }}>
                Publish another
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { onClose(); onSuccess() }}
              >
                View module →
              </button>
            </div>
          </div>
        ) : (
          <div style={s.twoCol}>
            {/* ── Left: instructions ── */}
            <div style={s.instructions}>
              <div style={s.instrTitle}>Packaging your module</div>

              <div style={s.step}>
                <span style={s.stepNum}>1</span>
                <div>
                  <div style={s.stepHead}>Structure your directory</div>
                  <div style={s.stepBody}>Place your <code style={s.code}>.tf</code> files in a flat folder. The folder name will become the module name.</div>
                  <pre style={s.pre}>{`my-vpc/
  readme.md
  main.tf
  variables.tf
  outputs.tf`}</pre>
                </div>
              </div>

              <div style={s.step}>
                <span style={s.stepNum}>2</span>
                <div>
                  <div style={s.stepHead}>Package as tar.gz</div>
                  <pre style={s.pre}>{`tar -czf my-vpc-1.0.0.tar.gz \\\n  -C my-vpc .`}</pre>
                  <div style={s.stepBody}>Name the archive <code style={s.code}>{'<name>-<version>.tar.gz'}</code> to auto-fill the form.</div>
                </div>
              </div>

              <div style={s.step}>
                <span style={s.stepNum}>3</span>
                <div>
                  <div style={s.stepHead}>Or publish via curl</div>
                  <pre style={s.pre}>{`curl -X PUT \\\n  -u "terramantle-bot:$TOKEN" \\\n  -H "Content-Type: application/octet-stream" \\\n  --data-binary @my-vpc-aws-1.0.0.tar.gz \\\n  "${curlUrl}"`}</pre>
                  <div style={s.tokenNote}>
                    Need a token?{' '}
                    <button type="button" style={s.tokenNoteLink} onClick={() => setShowTokenHelp(true)}>
                      See how to get one →
                    </button>
                    {' '}Use your personal session token for ad-hoc uploads, or create a{' '}
                    <strong style={{ color: 'var(--text)' }}>bot token</strong> under Bot tokens for CI pipelines.
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right: upload form ── */}
            <form onSubmit={handlePublish} style={s.form}>
              {/* Drop zone */}
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                style={{
                  ...s.dropzone,
                  borderColor: dragging ? 'var(--accent)' : file ? 'var(--green)' : 'var(--border2)',
                  background: dragging ? 'var(--accent-d)' : 'transparent',
                }}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".tar.gz,.tgz"
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) acceptFile(f) }}
                />
                {file ? (
                  <div>
                    <div style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: '0.82rem', marginBottom: 4 }}>{file.name}</div>
                    <div style={{ color: 'var(--text3)', fontSize: '0.72rem' }}>{(file.size / 1024).toFixed(1)} KB - click to change</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '1.2rem', marginBottom: 6 }}>⬡</div>
                    <div style={{ color: 'var(--text2)', fontSize: '0.82rem', marginBottom: 2 }}>Drop <code style={s.code}>.tar.gz</code> here</div>
                    <div style={{ color: 'var(--text3)', fontSize: '0.72rem' }}>or click to browse</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label className="field-label">Module name <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="input" placeholder="vpc" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
                    required pattern="[a-z][a-z0-9\-_]{0,63}" />
                </div>
                <div>
                  <label className="field-label">Version (semver) <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="input" placeholder="1.0.0" value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    required pattern="\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?" />
                </div>
              </div>

              {error && <div className="feedback-error" style={{ marginBottom: 14 }}>{error}</div>}

              {stage === 'uploading' && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: 'var(--accent)', transition: 'width 0.1s' }} />
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text3)', marginTop: 6 }}>Uploading… {progress}%</div>
                </div>
              )}

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={stage === 'uploading'}>
                {stage === 'uploading' ? <span className="spinner" /> : 'Publish'}
              </button>
            </form>
          </div>
        )}
      </Modal>
    </>
  )
}

function uploadWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
  authHeader: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.setRequestHeader('Authorization', authHeader)
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Publish failed: ${xhr.status}`))
    })
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
    xhr.send(file)
  })
}

const s: Record<string, React.CSSProperties> = {
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 32,
    alignItems: 'start',
  },
  instructions: {
    borderRight: '1px solid var(--border)',
    paddingRight: 32,
  },
  instrTitle: {
    fontFamily: 'var(--mono)',
    fontSize: '0.62rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--text3)',
    marginBottom: 20,
  },
  step: {
    display: 'flex',
    gap: 14,
    marginBottom: 24,
  },
  stepNum: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: 'var(--accent-d)',
    border: '1px solid var(--accent-d2)',
    color: 'var(--accent)',
    fontFamily: 'var(--mono)',
    fontSize: '0.65rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  stepHead: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: 6,
  },
  stepBody: {
    fontSize: '0.75rem',
    color: 'var(--text2)',
    lineHeight: 1.5,
    marginBottom: 8,
  },
  pre: {
    fontFamily: 'var(--mono)',
    fontSize: '0.7rem',
    color: 'var(--text2)',
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 5,
    padding: '10px 12px',
    overflowX: 'auto',
    whiteSpace: 'pre',
    marginBottom: 8,
  },
  code: {
    fontFamily: 'var(--mono)',
    fontSize: '0.82em',
    color: 'var(--accent)',
  },
  tokenNote: {
    marginTop: 10,
    fontSize: '0.73rem',
    color: 'var(--text3)',
    lineHeight: 1.5,
  },
  tokenNoteLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'var(--mono)',
    fontSize: '0.73rem',
    color: 'var(--accent)',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  dropzone: {
    border: '2px dashed',
    borderRadius: 'var(--radius-lg)',
    padding: '28px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    marginBottom: 16,
    transition: 'border-color 0.15s, background 0.15s',
  },
}
